class Wishlist {
  constructor() {
    this.core = window.SurmaWishlistCore
    if (!this.core) throw new Error('SurmaWishlistCore must load before wishlist.js')
    this.changedEvent = this.core.WISHLIST_CHANGED_EVENT
    this.products = []
    this.productIds = {}
    this.productNodes = {}
    this.pendingHandles = new Set()
    this.identityGeneration = 0
    // Last identity generation seen from the account app's events.
    this.accountGeneration = undefined

    this.pageTemplate = 'page.wishlist'
    this.addedClass = 'added-to-wishlist'
    this.hasItemClass = 'wishlist-has-item'
    this.selectors = {
      container: '.m-wishlist-page-content__wrapper',
      noProducts: '.m-wishlist-no-products',
      wrapper: '.m-wishlist-card',
      productCard: '.m-product-card',
      wishlistButton: '.m-wishlist-button',
      wishlistText: '.m-wishlist-button-text',
      removeButton: '.m-wishlist-remove-button',
      count: '.m-wishlist-count'
    }

    this.isWishlistPage = MinimogSettings.template === this.pageTemplate
    // The account app owns its page's wishlist requests, merges and sync.
    this.isAccountApp = !!document.getElementById('my-account-root')
    this.accountMode = this.hasAccountSession()
    this.products = this.accountMode ? [] : this.readGuestProducts()
    this.client = this.core.createWishlistClient(
      this.core.createWishlistFetchTransport({
        apiBaseUrl: this.apiBaseUrl,
        onUnauthorized: this.handleUnauthorized
      })
    )
    this.mergeGuestWishlist = this.core.createGuestWishlistMerger({
      storage: localStorage,
      merge: ids => this.client.merge(ids),
      resolveProductId: async handle => {
        try {
          return this.core.toProductGid(await this.resolveProductId(handle))
        } catch (error) {
          return null
        }
      },
      captureSession: () => {
        const generation = this.identityGeneration
        return () => this.hasAccountSession() && generation === this.identityGeneration
      },
      onMerged: this.notifyChanged
    })
    this.ready = this.init()
  }

  init = async () => {
    this.addEventToWishlistButtons()
    this.addEventToRemoveButtons()
    window.addEventListener(this.core.IDENTITY_CHANGED_EVENT, this.handleIdentityEvent)
    window.addEventListener(this.changedEvent, this.handleExternalChange)
    if (!this.isAccountApp) {
      this.stopSync = this.core.watchWishlistSync(window, reason => {
        if (reason === 'session') this.handleIdentityChanged()
        else if (reason === 'wishlist' || this.hasAccountSession()) this.handleExternalChange()
      }, this.core.webSessionKey(this.apiBaseUrl()))
    }
    await this.refresh()
  }

  apiBaseUrl = () => {
    const configured =
      (window.SurmaConfig && window.SurmaConfig.apiBaseUrl) ||
      (window.MinimogSettings && window.MinimogSettings.surmaApiBaseUrl) ||
      'https://dev.api.surma.in'
    return configured.toString().trim().replace(/\/$/, '')
  }

  hasAccountSession = () => {
    return this.core.hasWebSession(localStorage, this.core.webSessionKey(this.apiBaseUrl()))
  }

  handleUnauthorized = () => {
    this.core.clearWebSessionFlags(localStorage, sessionStorage, this.core.webSessionKey(this.apiBaseUrl()))
    window.dispatchEvent(new Event(this.core.SESSION_EXPIRED_EVENT))
  }

  readGuestProducts = () => {
    return this.core.readGuestHandles(localStorage)
  }

  saveGuestProducts = () => {
    this.products = this.core.saveGuestHandles(localStorage, this.products)
  }

  numericProductId = productId => {
    try {
      return this.core.numericProductId(productId)
    } catch (error) {
      return null
    }
  }

  loadAccountProducts = async (generation) => {
    const items = await this.client.list()
    if (generation !== this.identityGeneration || !this.hasAccountSession()) return
    this.products = []
    this.productIds = {}

    items.forEach(item => {
      const handle = item && typeof item.productHandle === 'string' ? item.productHandle.trim() : ''
      const productId = this.numericProductId(item && item.productId)
      if (!handle || !productId || this.products.includes(handle)) return
      this.products.push(handle)
      this.productIds[handle] = productId
    })
  }

  refresh = () => {
    this.refreshRequested = true
    if (!this.refreshRequest) {
      const request = this.runRefreshLoop()
      this.refreshRequest = request
      const clear = () => { if (this.refreshRequest === request) this.refreshRequest = null }
      request.then(clear, clear)
    }
    return this.refreshRequest
  }

  runRefreshLoop = async () => {
    while (this.refreshRequested && this.pendingHandles.size === 0) {
      this.refreshRequested = false
      await this.refreshState()
    }
  }

  refreshState = async () => {
    const generation = this.identityGeneration
    if (this.hasAccountSession()) {
      this.accountMode = true
      if (this.isAccountApp) return
      try {
        try {
          await this.mergeGuestWishlist()
        } catch (error) {
          // Preserve guest items and still load the account list. Sync retries.
          if (this.core.isWishlistHttpError(error, 401)) throw error
        }
        if (generation !== this.identityGeneration) return
        await this.loadAccountProducts(generation)
      } catch (error) {
        if (generation !== this.identityGeneration) return
        if (this.core.isWishlistHttpError(error, 401)) {
          this.accountMode = false
          this.products = this.readGuestProducts()
          this.productIds = {}
        } else {
          console.warn('[wishlist] could not load account wishlist', error)
        }
      }
    } else {
      this.accountMode = false
      this.products = this.readGuestProducts()
      this.productIds = {}
    }

    if (generation !== this.identityGeneration) return
    if (this.isWishlistPage) await this.renderWishlistPage()
    this.setWishlistButtonsState()
    this.updateWishlistCount()
  }

  handleIdentityChanged = () => {
    this.identityGeneration += 1
    this.ready = this.refresh()
  }

  // The account app fires identity events for every auth-state step and name
  // update. Only a login/logout (session flag flips) or an account switch
  // (its identity generation moves while the flag stays set) changes which
  // wishlist this page shows.
  handleIdentityEvent = event => {
    const generation = event && event.detail ? event.detail.generation : undefined
    let accountSwitched = false
    if (typeof generation === 'number') {
      accountSwitched = this.accountGeneration !== undefined && generation !== this.accountGeneration
      this.accountGeneration = generation
    }
    if (!accountSwitched && this.hasAccountSession() === this.accountMode) return
    this.handleIdentityChanged()
  }

  handleExternalChange = event => {
    if (event && event.detail && event.detail.source === 'theme') return
    this.ready = this.refresh()
  }

  notifyChanged = () => {
    this.core.notifyWishlistChanged(window, 'theme', this.accountMode)
  }

  resolveProductId = async (handle, candidate) => {
    const known = this.numericProductId(candidate) || this.productIds[handle]
    if (known) return known

    const response = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error('Could not resolve wishlist product')
    const product = await response.json()
    const productId = this.numericProductId(product && product.id)
    if (!productId) throw new Error('Wishlist product has no valid id')
    this.productIds[handle] = productId
    return productId
  }

  persistAccountChange = async (handle, productId, active) => {
    const id = await this.resolveProductId(handle, productId)
    if (active) {
      await this.client.add(id)
      this.productIds[handle] = id
      return
    }

    await this.client.remove(id)
    delete this.productIds[handle]
  }

  applyProductState = (handle, active) => {
    if (active) {
      if (!this.products.includes(handle)) this.products.push(handle)
    } else {
      this.products = this.products.filter(productHandle => productHandle !== handle)
    }
    if (!this.accountMode) this.saveGuestProducts()
    this.setButtonsForHandle(handle, active)
    this.updateWishlistCount()
  }

  changeProduct = async (handle, productId, active) => {
    if (!handle || this.pendingHandles.has(handle)) return false
    this.pendingHandles.add(handle)
    const wasActive = this.products.includes(handle)
    this.applyProductState(handle, active)

    try {
      if (this.accountMode) {
        await this.persistAccountChange(handle, productId, active)
      }
      this.notifyChanged()
      return true
    } catch (error) {
      if (this.core.isWishlistHttpError(error, 401)) {
        this.accountMode = false
        this.products = this.readGuestProducts()
        this.productIds = {}
        this.setWishlistButtonsState()
        this.updateWishlistCount()
      } else {
        this.applyProductState(handle, wasActive)
      }
      console.warn('[wishlist] could not update wishlist', error)
      return false
    } finally {
      this.pendingHandles.delete(handle)
      if (this.refreshRequested) this.ready = this.refresh()
    }
  }

  destroy = () => {
    this.stopSync?.()
    window.removeEventListener(this.core.IDENTITY_CHANGED_EVENT, this.handleIdentityEvent)
    window.removeEventListener(this.changedEvent, this.handleExternalChange)
  }

  setWishlistButtonsState = () => {
    const buttons = document.querySelectorAll(this.selectors.wishlistButton)
    buttons.forEach(btn => {
      const productHandle = btn && btn.dataset.productHandle
      const active = this.products.includes(productHandle)
      this.setButtonState(btn, active)

      if (active && this.isWishlistPage) {
        btn.classList.remove(this.selectors.wishlistButton.replace('.', ''))
        btn.classList.add(this.selectors.removeButton.replace('.', ''))
      }
    })
  }

  setButtonsForHandle = (handle, active) => {
    document.querySelectorAll(this.selectors.wishlistButton).forEach(btn => {
      if (btn && btn.dataset.productHandle === handle) {
        this.setButtonState(btn, active)
      }
    })
  }

  setButtonState = (btn, active) => {
    if (!btn || btn.classList.contains(this.addedClass) === active) return
    const wishlistText = btn.querySelector(this.selectors.wishlistText)
    btn.classList.toggle(this.addedClass, active)

    if (wishlistText) {
      const temp = wishlistText.dataset.revertText
      wishlistText.dataset.revertText = wishlistText.textContent
      wishlistText.textContent = temp
    }
  }

  updateWishlistCount = () => {
    const size = this.products.length
    document.querySelectorAll(this.selectors.count).forEach(elem => {
      elem.textContent = size
      elem.classList.toggle('m:hidden', size < 1)
    })
    document.body.classList.toggle(this.hasItemClass, size > 0)
  }

  addEventToWishlistButtons = () => {
    addEventDelegate({
      selector: this.selectors.wishlistButton,
      handler: async (event, button) => {
        event.preventDefault()
        await this.ready
        const handle = button && button.dataset.productHandle
        if (!handle) return
        const active = !this.products.includes(handle)
        await this.changeProduct(handle, button.dataset.productId, active)
      }
    })
  }

  addEventToRemoveButtons = () => {
    addEventDelegate({
      selector: this.selectors.removeButton,
      handler: async (event, button) => {
        event.preventDefault()
        await this.ready
        const handle = button && button.dataset.productHandle
        if (!handle) return
        const changed = await this.changeProduct(handle, button.dataset.productId, false)
        if (!changed) return
        const product = button.closest(this.selectors.wrapper)
        if (product) product.remove()
        if (this.products.length === 0) this.showNoProductsMessage()
      }
    })
  }

  wishlistRemoveButton(handle, productId) {
    const html = `<svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>`
    const button = document.createElement('div')
    button.classList.add('m-tooltip', 'm-wishlist-remove-button', 'm:block', 'md:m:hidden')
    button.setAttribute('data-product-handle', handle)
    if (productId) button.setAttribute('data-product-id', productId)
    button.innerHTML = html
    return button
  }

  renderWishlistPage = async () => {
    const container = document.querySelector(this.selectors.container)
    if (!container) return

    container.innerHTML = ''
    container.classList.remove('m:hidden')
    const noProducts = document.querySelector(this.selectors.noProducts)
    if (noProducts) noProducts.classList.add('m:hidden')
    this.productNodes = {}
    let noItemAvailable = true

    await Promise.all(
      this.products.map(async handle => {
        try {
          const url = formatUrl('products', handle, 'view=grid-card-item')
          const productHtml = await fetchCache(url)
          const item = document.createElement('div')
          item.classList.add('m:hidden', 'm:column', 'm-wishlist-card')
          item.innerHTML = productHtml
          if (!item.querySelector(this.selectors.productCard)) return
          noItemAvailable = false
          item.appendChild(this.wishlistRemoveButton(handle, this.productIds[handle]))
          this.productNodes[handle] = item
        } catch (error) {
          console.warn(`[wishlist] could not render ${handle}`, error)
        }
      })
    )

    this.products.forEach(handle => {
      const product = this.productNodes[handle]
      if (!product) return
      container.appendChild(product)
      if (MinimogTheme.CompareProduct) {
        MinimogTheme.CompareProduct.setCompareButtonsState()
      }
      product.classList.remove('m:hidden')
    })

    if (noItemAvailable) this.showNoProductsMessage()
    else this.setWishlistButtonsState()
    container.classList.add('is-visible')
  }

  showNoProductsMessage = () => {
    const container = document.querySelector(this.selectors.container)
    const noProducts = document.querySelector(this.selectors.noProducts)
    if (container) container.classList.add('m:hidden')
    if (noProducts) noProducts.classList.remove('m:hidden')
  }
}

MinimogTheme.Wishlist = new Wishlist()
